import { useState, useEffect } from "react"
import { useParams } from "react-router-dom"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { apiTenant } from "../lib/api"

interface Employee {
  id: string
  employeeNo: string
  workerType: string
  fullName: string
  nic: string | null
  email: string | null
  phone: string | null
  addressLine1: string | null
  addressLine2: string | null
  city: string | null
  epfNo: string | null
  joinedAt: string
  status: string
}

export function MyProfile() {
  const { slug = "" } = useParams<{ slug: string }>()
  const qc = useQueryClient()
  const me = useQuery({
    queryKey: ["my-me", slug],
    enabled: !!slug,
    queryFn: () => apiTenant<{ employee: Employee }>(slug, "/me"),
  })

  const [form, setForm] = useState({ email: "", phone: "", addressLine1: "", addressLine2: "", city: "" })
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState("")

  // Hydrate form when /me lands.
  useEffect(() => {
    if (me.data) {
      const e = me.data.employee
      setForm({
        email: e.email ?? "",
        phone: e.phone ?? "",
        addressLine1: e.addressLine1 ?? "",
        addressLine2: e.addressLine2 ?? "",
        city: e.city ?? "",
      })
    }
  }, [me.data])

  const update = useMutation({
    mutationFn: () =>
      apiTenant(slug, "/me", {
        method: "PATCH",
        body: JSON.stringify({
          email: form.email.trim() || null,
          phone: form.phone.trim() || null,
          addressLine1: form.addressLine1.trim() || null,
          addressLine2: form.addressLine2.trim() || null,
          city: form.city.trim() || null,
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-me", slug] })
      setSaved(true)
      setError("")
      setTimeout(() => setSaved(false), 2000)
    },
    onError: (err) => setError(err instanceof Error ? err.message : "Failed"),
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError("")
    update.mutate()
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">My profile</h1>

      {me.isLoading && <div className="text-sm text-slate-400">Loading…</div>}
      {me.error && <div className="text-sm text-red-600">{(me.error as Error).message}</div>}

      {me.data && (
        <>
          {/* Read-only summary */}
          <div className="bg-white border border-slate-200 rounded p-4 space-y-2">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <ReadField label="Full name" value={me.data.employee.fullName} />
              <ReadField label="Employee #" value={me.data.employee.employeeNo} mono />
              <ReadField label="Worker type" value={me.data.employee.workerType.replace("_", " ")} />
              <ReadField label="Status" value={me.data.employee.status} />
              <ReadField label="Joined" value={me.data.employee.joinedAt.slice(0, 10)} mono />
              <ReadField label="NIC" value={me.data.employee.nic ?? "—"} mono />
              <ReadField label="EPF #" value={me.data.employee.epfNo ?? "—"} mono />
            </div>
            <div className="text-[11px] text-slate-400 pt-2 border-t border-slate-100">
              Name, employee #, NIC, EPF, salary and bank details are managed by HR — contact them to change anything not editable below.
            </div>
          </div>

          {/* Editable contact info */}
          <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded p-4 space-y-3">
            <div className="text-sm font-medium text-slate-700 mb-2">Contact info</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Email">
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="form-input"
                />
              </Field>
              <Field label="Phone">
                <input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="form-input"
                />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Address line 1">
                <input
                  value={form.addressLine1}
                  onChange={(e) => setForm({ ...form, addressLine1: e.target.value })}
                  className="form-input"
                />
              </Field>
              <Field label="Address line 2">
                <input
                  value={form.addressLine2}
                  onChange={(e) => setForm({ ...form, addressLine2: e.target.value })}
                  className="form-input"
                />
              </Field>
            </div>
            <Field label="City">
              <input
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                className="form-input"
              />
            </Field>
            {error && <div className="text-sm text-red-600">{error}</div>}
            <div className="flex items-center justify-end gap-3">
              {saved && <span className="text-xs text-emerald-700">Saved ✓</span>}
              <button
                type="submit"
                disabled={update.isPending}
                className="px-3 py-1.5 bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 text-white text-sm font-medium rounded"
              >
                {update.isPending ? "Saving…" : "Save"}
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  )
}

function ReadField(props: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{props.label}</div>
      <div className={`text-sm ${props.mono ? "font-mono" : ""}`}>{props.value}</div>
    </div>
  )
}

function Field(props: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-slate-600 mb-1">{props.label}</div>
      {props.children}
    </label>
  )
}
